import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import useMembers from '@/hooks/useMembers';

import { useComparisonMode } from './useComparisonMode';
import { useHumanAgentsQueryVariables } from './useHumanAgentsQueryVariables';
import { selectAnalyticsDates } from '../redux/selectors';
import { getComparisonDates } from '../utils/getComparisonDates';

interface HumanAgentsPerformanceRow {
  agent_id: string;
  agent_name: string | null;
  total_conversations: number | null;
  avg_handling_seconds: number | null;
  avg_response_seconds: number | null;
  avg_resolution_seconds: number | null;
  avg_csat_human_agent: number | null;
  avg_user_rating: number | null;
}

interface QueryResponse {
  rows: HumanAgentsPerformanceRow[];
}

export interface HumanAgentPerformanceMetrics {
  sessions: number;
  handlingSeconds: number;
  responseSeconds: number;
  resolutionSeconds: number;
  userCsat: number;
  aiCsat: number;
}

export interface HumanAgentPerformanceRow extends HumanAgentPerformanceMetrics {
  id: string;
  name: string;
  previous: HumanAgentPerformanceMetrics | null;
}

const PERFORMANCE_FIELDS = `
  agent_id
  agent_name
  total_conversations
  avg_handling_seconds
  avg_response_seconds
  avg_resolution_seconds
  avg_csat_human_agent
  avg_user_rating
`;

export const GET_HUMAN_AGENTS_CONVERSATIONS_BY_AGENT = gql`
  query HumanAgentsConversationsByAgent(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_conversations_by_agent(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${PERFORMANCE_FIELDS}
    }
  }
`;

const GET_HUMAN_AGENTS_CONVERSATIONS_BY_AGENT_COMPARISON = gql`
  query HumanAgentsConversationsByAgentComparison(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_conversations_by_agent(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${PERFORMANCE_FIELDS}
    }
  }
`;

const toMetrics = (row: HumanAgentsPerformanceRow): HumanAgentPerformanceMetrics => ({
  sessions: row.total_conversations ?? 0,
  handlingSeconds: Math.round(row.avg_handling_seconds ?? 0),
  responseSeconds: Math.round(row.avg_response_seconds ?? 0),
  resolutionSeconds: Math.round(row.avg_resolution_seconds ?? 0),
  userCsat: row.avg_user_rating ?? 0,
  aiCsat: row.avg_csat_human_agent ?? 0,
});

export const useHumanAgentsPerformance = ({ skip: forceSkip = false } = {}) => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const { variables, skip: baseSkip } = useHumanAgentsQueryVariables();
  const skip = baseSkip || forceSkip;
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useHumanAgentsQueryVariables(comparisonDates);
  const { members } = useMembers();

  const { loading, data, error } = useQuery<QueryResponse>(
    GET_HUMAN_AGENTS_CONVERSATIONS_BY_AGENT,
    { variables, skip }
  );

  const { loading: comparisonLoading, data: comparisonData } = useQuery<QueryResponse>(
    GET_HUMAN_AGENTS_CONVERSATIONS_BY_AGENT_COMPARISON,
    { variables: comparisonVariables, skip: skip || !isComparing }
  );

  const membersById = useMemo(() => new Map(members?.map((m) => [m.user_id, m]) ?? []), [members]);

  const previousById = useMemo(
    () =>
      new Map<string, HumanAgentPerformanceMetrics>(
        (comparisonData?.rows ?? []).map((row) => [row.agent_id, toMetrics(row)])
      ),
    [comparisonData?.rows]
  );

  const rows = useMemo<HumanAgentPerformanceRow[]>(
    () =>
      (data?.rows ?? []).map((row) => {
        const member = membersById.get(row.agent_id);
        const metrics = toMetrics(row);
        return {
          id: row.agent_id,
          name: member?.name?.trim() || member?.email || row.agent_name?.trim() || row.agent_id,
          ...metrics,
          previous: previousById.get(row.agent_id) ?? null,
        };
      }),
    [data, membersById, previousById]
  );

  return { isLoading: loading || (isComparing && comparisonLoading), error, data: rows };
};
